import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Activity, Calendar } from "lucide-react";
import BloodTestTrackExport from "@/components/blood-tests/BloodTestTrackExport";

const KIND_LABELS = {
  baseline: { label: "Référence", className: "bg-indigo-100 text-indigo-700" },
  followup: { label: "Contrôle", className: "bg-sky-100 text-sky-700" },
};

function formatDate(d) {
  if (!d || d === "unknown") return "—";
  try {
    return new Date(d + "T12:00:00").toLocaleDateString("fr-FR");
  } catch {
    return d;
  }
}

function MarkerChart({ marker, onSelectReport }) {
  const chartData = useMemo(() => {
    return (marker.points || [])
      .filter(p => p.value_num != null)
      .map(p => ({
        date: formatDate(p.date),
        rawDate: p.date,
        value: p.value_num,
        display: `${p.value}${p.unit ? ` ${p.unit}` : ""}`,
        status: p.status,
        report_id: p.report_id,
      }));
  }, [marker]);

  if (chartData.length < 2) {
    return (
      <Card className="border-slate-100">
        <CardContent className="p-4 text-center py-6">
          <p className="text-xs text-slate-400">{marker.name} — pas assez de points numériques</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-100">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-sm font-semibold text-slate-700 truncate">{marker.name}</p>
          {marker.unit && <span className="text-xs text-slate-400 shrink-0">{marker.unit}</span>}
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart
            data={chartData}
            onClick={(e) => {
              const pid = e?.activePayload?.[0]?.payload?.report_id;
              if (pid && onSelectReport) onSelectReport(pid);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={["auto", "auto"]} />
            <Tooltip
              contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", fontSize: "12px" }}
              formatter={(v, _n, props) => [props.payload.display, marker.name]}
            />
            <Line type="monotone" dataKey="value" stroke="#dc2626" strokeWidth={2} dot={{ r: 4, fill: "#dc2626", cursor: "pointer" }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export default function BloodTestTrackEvolution({ trackId, onSelectReport, studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState("");

  useEffect(() => {
    if (!trackId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/blood-tests/tracks/${trackId}/evolution`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [trackId, studentId]);

  const chartableMarkers = useMemo(() => {
    if (!data?.markers) return [];
    return data.markers.filter(m => (m.points || []).filter(p => p.value_num != null).length >= 2);
  }, [data]);

  useEffect(() => {
    if (chartableMarkers.length > 0 && !selectedMarker) {
      setSelectedMarker(chartableMarkers[0].key);
    }
  }, [chartableMarkers, selectedMarker]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-slate-400">Impossible de charger l'évolution du suivi</p>;
  }

  const selected = chartableMarkers.find(m => m.key === selectedMarker) || chartableMarkers[0];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-red-100 bg-red-50/40 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">{data.track?.name}</h2>
            {data.track?.description && (
              <p className="text-sm text-slate-500 mt-1">{data.track.description}</p>
            )}
          </div>
          <Badge className="bg-red-100 text-red-700">
            {data.track?.report_count || 0} bilan{(data.track?.report_count || 0) > 1 ? "s" : ""}
          </Badge>
        </div>
        {data.track?.last_lab_date && (
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Dernier prélèvement : {formatDate(data.track.last_lab_date)}
          </p>
        )}
      </div>

      <BloodTestTrackExport
        trackId={trackId}
        trackName={data.track?.name}
        reportCount={data.track?.report_count}
      />

      {chartableMarkers.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Activity className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-slate-700">Évolution des marqueurs</span>
            {chartableMarkers.length > 1 && (
              <Select value={selectedMarker} onValueChange={setSelectedMarker}>
                <SelectTrigger className="w-[220px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {chartableMarkers.map(m => (
                    <SelectItem key={m.key} value={m.key}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {selected && <MarkerChart marker={selected} onSelectReport={onSelectReport} />}
          {chartableMarkers.length > 1 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {chartableMarkers.slice(0, 4).map(m => (
                <MarkerChart key={m.key} marker={m} onSelectReport={onSelectReport} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-sm text-slate-500">
            Ajoutez au moins deux bilans analysés dans ce suivi pour voir l'évolution des marqueurs.
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Bilans du suivi</h3>
        <div className="space-y-2">
          {(data.reports || []).map(r => {
            const kind = KIND_LABELS[r.report_kind];
            return (
              <button
                key={r.report_id}
                type="button"
                onClick={() => onSelectReport?.(r.report_id)}
                className="w-full text-left rounded-xl border border-slate-100 bg-white p-3 hover:border-red-200 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900 text-sm truncate">{r.title}</span>
                  {kind && <Badge className={kind.className}>{kind.label}</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {r.lab_date ? formatDate(r.lab_date) : "Date inconnue"}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}